import { WorkstyleDimension } from './types';

// =============================================================================
// WERKSTIJL / WORKSTYLE TEST (Kolbe-inspired)
// =============================================================================
//
// 8 vragen die meten HOE je werkt (niet WAT je kunt):
// - Fact Finder: Hoeveel onderzoek doe je? (0-10)
// - Follow Thru: Hoe systematisch ben je? (0-10)
// - Quick Start: Hoe snel neem je risico's? (0-10)
// - Implementor: Werk je hands-on of conceptueel? (0-10)
//
// =============================================================================

export interface WorkstyleQuestion {
  id: number;
  dimension: WorkstyleDimension;
  question: string;
  leftLabel: string;   // 0-3: Laag
  rightLabel: string;  // 7-10: Hoog
}

export const WORKSTYLE_QUESTIONS: WorkstyleQuestion[] = [
  // FACT FINDER (2 vragen)
  {
    id: 1,
    dimension: 'fact_finder',
    question: 'Bij een nieuw idee of project...',
    leftLabel: 'Duik ik er direct in, onderweg leer ik wel',
    rightLabel: 'Doe ik eerst grondig onderzoek en verzamel ik info'
  },
  {
    id: 2,
    dimension: 'fact_finder',
    question: 'Voordat ik een beslissing neem...',
    leftLabel: 'Vertrouw ik op mijn gevoel en intuïtie',
    rightLabel: 'Wil ik alle feiten en data hebben'
  },
  
  // FOLLOW THRU (2 vragen)
  {
    id: 3,
    dimension: 'follow_thru',
    question: 'Ik werk het beste...',
    leftLabel: 'Met vrijheid en flexibiliteit, ik improviseer',
    rightLabel: 'Met duidelijke structuur en stappenplan'
  },
  {
    id: 4,
    dimension: 'follow_thru',
    question: 'Als ik een project start...',
    leftLabel: 'Begin ik en zie ik wel waar het heen gaat',
    rightLabel: 'Maak ik eerst een gedetailleerd plan'
  },
  
  // QUICK START (2 vragen)
  {
    id: 5,
    dimension: 'quick_start',
    question: 'Bij nieuwe kansen...',
    leftLabel: 'Denk ik er lang over na en weeg ik af',
    rightLabel: 'Spring ik er direct op, geen tijd verliezen'
  },
  {
    id: 6,
    dimension: 'quick_start',
    question: 'Met risico\'s...',
    leftLabel: 'Ben ik voorzichtig en vermijd ik ze liever',
    rightLabel: 'Ga ik graag het experiment aan'
  },
  
  // IMPLEMENTOR (2 vragen)
  {
    id: 7,
    dimension: 'implementor',
    question: 'Ik werk liever...',
    leftLabel: 'Met concepten, ideeën en strategieën',
    rightLabel: 'Hands-on, met tastbare dingen'
  },
  {
    id: 8,
    dimension: 'implementor',
    question: 'Bij het oplossen van problemen...',
    leftLabel: 'Denk ik abstract en bedenk ik concepten',
    rightLabel: 'Bouw ik praktisch een prototype of oplossing'
  }
];

// =============================================================================
// WERKSTIJL INTERPRETATIE
// =============================================================================

export interface WorkstyleInterpretation {
  score: number;
  label: string;
  description: string;
  entrepreneurialImpact: string;
}

export const WORKSTYLE_INTERPRETATIONS: Record<WorkstyleDimension, {
  low: WorkstyleInterpretation;
  medium: WorkstyleInterpretation;
  high: WorkstyleInterpretation;
}> = {
  fact_finder: {
    low: {
      score: 3,
      label: 'Actiegericht',
      description: 'Je duikt direct in zonder veel onderzoek. Je leert onderweg en vertrouwt op ervaring.',
      entrepreneurialImpact: 'Goed voor snelle execution, maar let op dat je niet essentiële info mist.'
    },
    medium: {
      score: 5,
      label: 'Balanced',
      description: 'Je doet redelijk onderzoek, maar niet overdreven. Je balanceert tussen info verzamelen en actie.',
      entrepreneurialImpact: 'Goede balans tussen onderzoek en execution.'
    },
    high: {
      score: 8,
      label: 'Onderzoeker',
      description: 'Je doet grondig onderzoek voordat je start. Je wilt alle feiten en data hebben.',
      entrepreneurialImpact: 'Goede voorbereiding, maar pas op voor analysis paralysis.'
    }
  },
  follow_thru: {
    low: {
      score: 3,
      label: 'Flexibel',
      description: 'Je werkt graag met vrijheid en improviseert. Structuur voel je als een beperking.',
      entrepreneurialImpact: 'Goed voor creativiteit, maar projecten lopen risico niet af te komen.'
    },
    medium: {
      score: 5,
      label: 'Adaptief',
      description: 'Je kunt zowel gestructureerd als flexibel werken, afhankelijk van de situatie.',
      entrepreneurialImpact: 'Je past je aan aan wat nodig is.'
    },
    high: {
      score: 8,
      label: 'Gestructureerd',
      description: 'Je werkt graag met duidelijke plannen en systemen. Je maakt dingen systematisch af.',
      entrepreneurialImpact: 'Projecten komen af, maar flexibiliteit kan soms ontbreken.'
    }
  },
  quick_start: {
    low: {
      score: 3,
      label: 'Afwachtend',
      description: 'Je denkt lang na voordat je actie onderneemt. Je vermijdt liever risico\'s.',
      entrepreneurialImpact: 'Voorzichtig en doordacht, maar kansen kunnen je voorbij gaan.'
    },
    medium: {
      score: 5,
      label: 'Calculated Risk Taker',
      description: 'Je neemt risico\'s, maar alleen na goede afweging.',
      entrepreneurialImpact: 'Balans tussen voorzichtigheid en durf.'
    },
    high: {
      score: 8,
      label: 'Quick Starter',
      description: 'Je springt snel op nieuwe kansen. Je bent niet bang om risico\'s te nemen.',
      entrepreneurialImpact: 'Veel experimenten en snelle groei, maar let op overcommitment.'
    }
  },
  implementor: {
    low: {
      score: 3,
      label: 'Conceptueel',
      description: 'Je werkt liever met ideeën, concepten en strategieën. Abstract denken is je kracht.',
      entrepreneurialImpact: 'Goed voor strategie en visie, maar execution kan lastig zijn.'
    },
    medium: {
      score: 5,
      label: 'Hybride',
      description: 'Je kunt zowel conceptueel als praktisch werken.',
      entrepreneurialImpact: 'Veelzijdig inzetbaar.'
    },
    high: {
      score: 8,
      label: 'Hands-on',
      description: 'Je werkt graag hands-on met tastbare dingen. Je bouwt liever een prototype dan een plan.',
      entrepreneurialImpact: 'Snel prototypen en maken, maar strategisch overzicht kan ontbreken.'
    }
  }
};

// =============================================================================
// ENERGIE GIVERS & DRAINERS
// =============================================================================

export interface WorkstyleGuidance {
  energyGivers: string[];
  energyDrainers: string[];
  idealTasks: string[];
  avoidTasks: string[];
  delegateRecommendations: string[];
}

export function getWorkstyleGuidance(scores: { 
  fact_finder: number;
  follow_thru: number;
  quick_start: number;
  implementor: number;
}): WorkstyleGuidance {
  const guidance: WorkstyleGuidance = {
    energyGivers: [],
    energyDrainers: [],
    idealTasks: [],
    avoidTasks: [],
    delegateRecommendations: []
  };

  // FACT FINDER
  if (scores.fact_finder <= 3) {
    guidance.energyGivers.push('Snel experimenteren en leren onderweg');
    guidance.energyDrainers.push('Lange onderzoeksprojecten');
    guidance.idealTasks.push('Snelle prototypes maken');
    guidance.avoidTasks.push('Marktonderzoek');
    guidance.delegateRecommendations.push('Iemand die grondig research kan doen');
  } else if (scores.fact_finder >= 7) {
    guidance.energyGivers.push('Grondig onderzoek en data analyse');
    guidance.energyDrainers.push('Snel beslissingen nemen zonder info');
    guidance.idealTasks.push('Marktonderzoek en concurrentie analyse');
    guidance.avoidTasks.push('Snelle experimenten zonder plan');
  }

  // FOLLOW THRU
  if (scores.follow_thru <= 3) {
    guidance.energyGivers.push('Vrijheid en flexibiliteit');
    guidance.energyDrainers.push('Strakke structuren en protocollen');
    guidance.idealTasks.push('Creatief werk en innovatie');
    guidance.avoidTasks.push('Repetitieve administratie');
    guidance.delegateRecommendations.push('Iemand die structuur en systemen kan opzetten');
  } else if (scores.follow_thru >= 7) {
    guidance.energyGivers.push('Duidelijke plannen en systemen');
    guidance.energyDrainers.push('Chaos en gebrek aan structuur');
    guidance.idealTasks.push('Processen opzetten en optimaliseren');
    guidance.avoidTasks.push('Adhoc werk zonder plan');
  }

  // QUICK START
  if (scores.quick_start <= 3) {
    guidance.energyGivers.push('Zorgvuldige planning en voorbereiding');
    guidance.energyDrainers.push('Gedwongen snelle beslissingen');
    guidance.idealTasks.push('Strategische planning');
    guidance.avoidTasks.push('High-risk experimenten');
    guidance.delegateRecommendations.push('Iemand die snel kan experimenteren');
  } else if (scores.quick_start >= 7) {
    guidance.energyGivers.push('Nieuwe kansen en experimenten');
    guidance.energyDrainers.push('Wachten en lange planningsprocessen');
    guidance.idealTasks.push('Product launches en nieuwe projecten starten');
    guidance.avoidTasks.push('Lange termijn planning');
  }

  // IMPLEMENTOR
  if (scores.implementor <= 3) {
    guidance.energyGivers.push('Strategisch denken en concepten ontwikkelen');
    guidance.energyDrainers.push('Hands-on technisch werk');
    guidance.idealTasks.push('Business strategie en visie');
    guidance.avoidTasks.push('Bouwen en maken');
    guidance.delegateRecommendations.push('Iemand die hands-on kan bouwen/maken');
  } else if (scores.implementor >= 7) {
    guidance.energyGivers.push('Hands-on bouwen en maken');
    guidance.energyDrainers.push('Abstracte strategische discussies');
    guidance.idealTasks.push('Prototypes bouwen en productontwikkeling');
    guidance.avoidTasks.push('Lange strategische sessies');
  }

  return guidance;
}
